// Building blocks shared by the open-ended family's items-list calculators, taken from the
// shared ItemsListView calculator (question_views/components/items_list.coffee). The family
// has two of those calculators -- the sentiments / tags one next door, and combo's, which
// renders a real question and so needs the whole thing -- and everything they have in common
// lives here so the two cannot drift apart.
//
// Everything resolved off page / window stays with the caller and arrives as a parameter.

import { formatAmounts } from '@aytm/app/stats/lib/util/helpers'
import { openCommentsPanel, getSurveyCodeBooks } from '@aytm/app/stats/lib/stat-page'
import { SENTIMENTS } from '@aytm/app/stats/page/components/lib/constants'
import { LINK_ID_REG } from '@aytm/app/stats/page/question-views/components/lib/constants'
import { isCustomQuestion, questionIdToPosition, resolveQuestionId } from '@aytm/app/stats/page/components/lib/utils'

// Palette length: colour indices cycle modulo this size.
export const PALETTE_SIZE = 21

// Fallback swatch for a tag whose codebook carries no colour of its own.
const DEFAULT_TAG_COLOR = '88d1cd'

// A codebook-qualified tag reads `<codebook>: <tag>`; the panel filters on the bare tag.
const QUALIFIED_TAG = /^([^:]+): (.+)$/

// The query entry a tag row filters the comments panel by. A sentiment carries its numeric rank,
// a plain tag its codebook colour.
const calcTagQuery = ({ tag, type }) => {
  if ('sentiment' === type) {
    const { sentiment } = SENTIMENTS.find(v => v.value === tag) || {}
    return { type: 'sentiment', value: tag, label: `sentiment: ${tag}`, sentiment }
  }

  if ('tag' !== type) return null

  const match = tag.match(QUALIFIED_TAG)
  if (!match) return { type: 'tag', value: tag, label: tag, color: `#${DEFAULT_TAG_COLOR}` }

  const color = getSurveyCodeBooks().find(cb => cb.title === match[1])?.color || DEFAULT_TAG_COLOR

  return { type: 'tag', value: match[2], label: match[2], codebook: match[1], color: `#${color}` }
}

// A generated Xpert sub-question (`xq1_2`, or its chart block `xq1_2-1`), naming the Xpert question.
const GENERATED_XQ_ID = /^(xq\d+)_\d+(?:-\d+)?$/i

// The Comments page lists a generated sub-question's answers under the Xpert question that
// generated it, so addressing the sub-question itself selected nothing there.
export const calcCommentsQuestion = (q, id) => {
  const generatedXq = String(q).match(GENERATED_XQ_ID)
  if (generatedXq) return generatedXq[1]

  return isCustomQuestion(q) ? id : String(resolveQuestionId(id)).toLowerCase()
}

// An expanded answer draws its own block under `<question>-<answer>` (`vq2-5`, `cl2-5`). The Comments
// page lists that answer as `vq2a5`, so the suffixed id matched no option there.
const CUSTOM_ANSWER_ID = /^([a-z]+\d+)-(\d+)$/i

// The comments-panel handler for an open-ended row, built once from the question's identity the
// way the heatmap block builds its own. The row's tag list arrives per call: the sentiments and
// tags surfaces resolve their own rows, so the block cannot close over them. A block drawn under a
// row of another question (a heatmap's pin group or cluster) is handed that row's `scope`, the chips
// narrowing the panel to it, and opens inside it.
export const buildOnCommentsClick = ({ id, questionId, link, isReportXpert, batch, answerPosition, scope }) => {
  return (event, position, answerVariants = []) => {
    const q = isReportXpert && LINK_ID_REG.test(link || '') ? questionIdToPosition(link) : questionId || id

    const answerVariant = answerVariants.find(av => av.position === position) || {}

    // the question is addressed by whichever of the two the shell handed down -- a bare position
    // converts, an id is already one, and coercing an id to a number found no question and threw
    const question = calcCommentsQuestion(q, id)
    const customAnswer = String(question).match(CUSTOM_ANSWER_ID)
    const path = [customAnswer ? customAnswer[1] : question]
    // an ordinary question's id loses its answer on the way to the question id, so the expanded
    // answer's block names its answer itself
    const tagAnswer = answerPosition ?? customAnswer?.[2]
    const query = [...(scope?.(path[0]) || [])]

    if (Object.hasOwn(answerVariant, 'tag')) {
      const entry = calcTagQuery(answerVariant)
      if (entry) query.push(entry)

      if (batch) path.push(`a${Number(batch.position)}`)
      else if (null != tagAnswer) path.push(`a${Number(tagAnswer)}`)
    } else {
      if (batch) path.push(`sq${batch.position}`)

      path.push(`a${answerVariant.position}`)
    }

    openCommentsPanel({ event, path: path.join(''), query, isReportXpert })
  }
}

// Ties break by row position so equal values keep a stable order.
export const byPosition = (aRow, bRow, dir) => dir * (aRow.position - bRow.position)
export const byCellValue = (index, key) => (aRow, bRow, dir) =>
  dir * (aRow.cells[index][key] + aRow.position / 10000 - (bRow.cells[index][key] + bRow.position / 10000))

// Whether the payload reports a base alongside its totals.
export const hasBaseIn = values =>
  Object.hasOwn(values, 'base') ||
  Object.keys(values).some(key => null !== values[key] && Object.hasOwn(values[key] || {}, 'base'))

// An untagged answer is indifferent under every rule: the screener says nothing about it, so
// choosing it changes nothing about who continues. Only a tag carries a consequence, and what a
// tag means is the rule's business -- 0 disqualifies under rules 1 and 3, while under rule 2 a
// positive tag qualifies. Read the tag against its rule, never on its own.
export const screenerState = (preType, preTag) => {
  if (null == preTag) return [1, 2, 3].includes(preType) ? 'indifferent' : null
  if (1 === preType) return 0 === preTag ? 'ejected' : 'accepted'
  if (2 === preType) return 0 < preTag ? 'accepted' : 'indifferent'
  if (3 === preType) return 0 === preTag ? 'ejected' : 'indifferent'

  return null
}

// The answer's own control, drawn the way the editor draws it: a radio where the question takes one
// answer, a checkbox where it takes several.
export const screenerKind = type => ('rb' === type ? 'rb' : 'cb')

export const buildProgressTitle = ({ answer, answerType = 'amount', type, rType = 'cases', hasBase, hideTotal }) => {
  if ('amount' !== answerType) return hideTotal ? '' : `<small>(N=${formatAmounts(answer, 'total', 0)})</small>`
  if (hasBase) return `<small>(based on ${formatAmounts(answer, ['base', 'total'])} responses)</small>`
  if (hideTotal) return ''

  if ('oex' === type) {
    const total = formatAmounts(answer, 'cases' === rType ? 'total' : 'respTotal', 0)
    return `<small>(based on ${total} responses)</small>`
  }

  const label = 'cases' === rType ? 'Respondents' : 'Responses'
  return `${label}:<small>${formatAmounts(answer, 'cases' === rType ? 'total' : 'respTotal')}</small>`
}

// A question's answer has arrived once it is an object: not missing, and not the loading sentinel.
export const answerSettled = answer => null != answer && 'object' === typeof answer

// The count a list row prints. The stats service leaves out the count of an answer nobody chose,
// so once the question's answer has arrived a missing count is that zero; with no answer at all
// there is no count to state, and a count the answer states as null stays unstated.
export const calcCount = ({ settled, variantAnswer, answerType }) =>
  settled && !Object.hasOwn(variantAnswer, answerType) ? 0 : variantAnswer[answerType]

// The value cell carries an amount, a percentage, or both, depending on the format flags and
// on whether the payload reports per-respondent percentages.
export const valueCell = ({
  item,
  answerType = 'amount',
  rType = 'cases',
  isLoading,
  settled,
  ignoreAmount,
  ignorePercents
}) => {
  const cell = { loading: isLoading }
  const count = calcCount({ settled, variantAnswer: item, answerType })

  if (ignorePercents) {
    cell.amount = item[answerType] || 0
    return cell
  }

  if (ignoreAmount) {
    cell.percents = item.percents || 0
    return cell
  }

  // No list drawn from here belongs to a pre-qualification question, so no screener withholds a row:
  // every answer states its share and count.
  if ('cases' === rType) {
    cell.percents = item.percents || 0
    cell.amount = count

    return cell
  }

  if (undefined !== item.respPercents) {
    cell.percents = item.respPercents || 0
    cell.amount = count
    return cell
  }

  cell.percents = 0
  cell.amount = 0

  return cell
}

// The trait breakdown that expands under a row, shared by every view that splits a row this way.
export { calcTraitTable } from '@aytm/app/stats/page/question-views/components/lib/trait-table'

// The column a picked index stands for. Nested column groups count their leaves, so the walk
// descends a level at a time; the recursion stops once nothing nests, which the base it was
// ported from did not, and looped forever on an index past the last column.
const findPickedCol = (cols, colIndex, offset) => {
  if (colIndex < offset + cols.length) return cols[colIndex - offset]
  if (!cols.some(col => col.cols?.length)) return null

  return findPickedCol(
    cols.flatMap(col => col.cols || [{}]),
    colIndex,
    offset + cols.length
  )
}

// The sort token a header pick produces: the title column sorts by item, the progress column
// by value, and a named value column by its own name. The inverse of `applySorted`.
export const calcSortOrder = (cols = [], colIndex, dir) => {
  const col = findPickedCol(cols, colIndex, 0)
  if (!col) return null

  const suffix = 1 === dir ? 'asc' : 'desc'

  if ('title' === col.type) return `i${suffix}`
  // a value column with no param of its own has nothing to name, so it orders by value like the
  // progress column does -- this has to be asked before the value arm, or the order is built out
  // of an undefined param
  if ('progress' === col.type || !col.param) return `v${suffix}`
  if ('value' === col.type) return `${col.param}-${suffix}`

  return null
}

// Which column the persisted sort token points at, and in which direction. A token carrying a
// column name selects that column; otherwise the leading letter picks the title or the value
// column. An unresolvable token falls back to the last column, as the calculator always did.
export const applySorted = (cols, sortOrder = 'iasc') => {
  const parts = sortOrder.split('-')

  let colIndex
  if (1 < parts.length) {
    colIndex = cols.findIndex(col => col.param === parts[0])
  } else {
    const colType = ('i' === sortOrder[0] && 'title') || ('v' === sortOrder[0] && 'progress')
    colIndex = cols.findIndex(col => colType === col.type)
  }
  if (0 > colIndex) colIndex = cols.length - 1

  const dir = 2 === parts.length ? ('asc' === parts[1] ? 1 : -1) : 'asc' === sortOrder.slice(1) ? 1 : -1

  return cols.map((col, index) => {
    const { sorted, ...rest } = col
    return index === colIndex ? { ...rest, sorted: dir } : rest
  })
}
